export type CupraConversationStateId =
  | 'greeting'
  | 'needs_discovery'
  | 'recommendation'
  | 'explanation'
  | 'questions_and_closing'
  | 'farewell';

export type CupraConversationState = {
  id: CupraConversationStateId;
  description: string;
  instructions: string[];
  examples: string[];
  transitions: Array<{ next_state: CupraConversationStateId; condition: string }>;
};

export const CUPRA_CONVERSATION_STATES: CupraConversationState[] = [
  {
    id: 'greeting',
    description: 'Open proactively and create an energetic CUPRA first impression.',
    instructions: [
      'Greet proactively in a bold but friendly CUPRA voice.',
      'Position the talk as a short, personalized discovery to find the right CUPRA model.',
      'Ask one concise question that invites the user into the conversation.',
      'After this first greeting turn, immediately call set_conversation_state with next_state="needs_discovery".'
    ],
    examples: [
      'Willkommen bei CUPRA. Lust auf ein Modell, das wirklich zu deinem Rhythmus passt?',
      'Bereit fuer deinen naechsten Impuls auf der Strasse? Dann starten wir direkt. Bereit?'
    ],
    transitions: [
      {
        next_state: 'needs_discovery',
        condition: 'Greeting turn is complete.'
      }
    ]
  },
  {
    id: 'needs_discovery',
    description: 'Discover user needs in 2-3 turns and gather recommendation signals.',
    instructions: [
      'Use short follow-up questions to gather priorities: budget, use case, performance style, drivetrain preference.',
      'If the user response is unclear, off-topic, or does not answer the question, acknowledge briefly and ask the same question again in a different way. Do not count that turn toward the 2-3 turn limit.',
      'Only move to recommendation once at least two concrete preferences are known. Do not infer preferences from vague or hallucinated input. Only count preferences that are explicitly about the car — personal statements about the user themselves (e.g. "my athletic performance is not great") do not count.',
      'Summarize confirmed needs in plain language before calling the recommendation tool.'
    ],
    examples: [
      'Faehrst du eher City, Langstrecke oder sportlich am Wochenende?',
      'Welche Rolle spielt fuer dich Reichweite, Preis und Performance?',
      'Wenn du priorisieren musst: Komfort, Design oder pure Dynamik?'
    ],
    transitions: [
      {
        next_state: 'recommendation',
        condition: 'At least two concrete user preferences are known (use case, style, budget, or drivetrain) and have been explicitly confirmed by the user. Unclear or off-topic answers do not count.'
      }
    ]
  },
  {
    id: 'recommendation',
    description: 'Recommend one best matching CUPRA model based on user needs.',
    instructions: [
      'Before calling get_cupra_recommendation, say one short bridging sentence."',
      'Call get_cupra_recommendation with a concise summary of user needs.',
      'Present one primary model recommendation first, with confidence and clarity.',
      'Do not list multiple alternatives unless the user asks for options.'
    ],
    examples: [
      'Basierend auf deinen Prioritaeten passt der CUPRA Tavascan am besten zu dir.',
      'Dein Match ist der CUPRA Born: vollelektrischer Impuls, dynamisch und alltagstauglich.'
    ],
    transitions: [
      {
        next_state: 'explanation',
        condition: 'Primary model recommendation has been clearly stated AND the user has reacted — with interest, a question, or even skepticism. Do not transition before the user responds.'
      }
    ]
  },
  {
    id: 'explanation',
    description: 'Explain why the model fits and give short, relevant details.',
    instructions: [
      'Ground details using search_cupra_data and cite concrete specs or equipment from data.',
      'Explain fit by linking each detail to a user need.',
      'Keep it voice-friendly: max 2-3 highlights, no long lists.'
    ],
    examples: [
      'Er passt, weil du Reichweite plus Dynamik suchst: starke Leistung, klare Alltagstauglichkeit und markantes SUV-Coupe Design.',
      'Fuer dich zaehlt Effizienz mit Charakter - genau da trifft dieses Modell ins Schwarze.'
    ],
    transitions: [
      {
        next_state: 'questions_and_closing',
        condition: 'Fit explanation is complete AND the user has given a clear verbal reaction — a question, a positive or negative statement, or an explicit acknowledgment. Short non-verbal sounds like "mm", "mm-mm", "uh-huh" do not qualify — ask a direct follow-up question to get a real response first.'
      }
    ]
  },
  {
    id: 'questions_and_closing',
    description:
      'Answer grounded questions, then proactively drive toward test drive lead capture with QR.',
    instructions: [
      'Invite questions and answer each with grounded data via search_cupra_data.',
      'After around two user questions, ask proactively about booking a test drive.',
      'If user agrees, call show_qr_code with the official CUPRA test drive URL and confirm it is visible.'
    ],
    examples: [
      'Wenn du magst, klaeren wir jetzt deine wichtigsten Fragen - ich halte es kurz und konkret.',
      'Soll ich dir direkt den QR-Code zur Probefahrt einblenden?'
    ],
    transitions: [
      {
        next_state: 'farewell',
        condition: 'The test-drive step has been explicitly resolved: either the QR code was shown and the user acknowledged it, or the user clearly declined. At least one factual question has been addressed. Do not transition just because the user said a short negative — confirm they are done first.'
      }
    ]
  },
  {
    id: 'farewell',
    description: 'Close politely and leave a strong final brand impression.',
    instructions: [
      'Thank the user for the conversation.',
      'Wish a great day in one short sentence.',
      'Close warmly, without adding new content.'
    ],
    examples: [
      'Danke fuer das starke Gespraech - ich wuensche dir einen grossartigen Tag.',
      'Vielen Dank und viel Freude bei deiner CUPRA Entscheidung. Hab einen guten Tag.'
    ],
    transitions: []
  }
];

export const CUPRA_CONVERSATION_STATE_ORDER: CupraConversationStateId[] =
  CUPRA_CONVERSATION_STATES.map((state) => state.id);

export const INITIAL_CUPRA_STATE: CupraConversationStateId = 'greeting';

let currentCupraStateId: CupraConversationStateId = INITIAL_CUPRA_STATE;

export function getCurrentCupraStateId() {
  return currentCupraStateId;
}

export function setCurrentCupraStateId(stateId: CupraConversationStateId) {
  currentCupraStateId = stateId;
}

export function getCupraStateById(stateId: CupraConversationStateId) {
  return CUPRA_CONVERSATION_STATES.find((state) => state.id === stateId);
}

export function getCupraNextStateIds(stateId: CupraConversationStateId) {
  const state = getCupraStateById(stateId);
  return state ? state.transitions.map((transition) => transition.next_state) : [];
}

export function buildCupraStateSection(stateId: CupraConversationStateId) {
  const state = getCupraStateById(stateId);
  if (!state) {
    return `Current state: ${stateId} (unknown).`;
  }

  const instructionLines = state.instructions.map((line) => `- ${line}`).join('\n');
  const exampleLines = state.examples.map((line) => `- ${line}`).join('\n');
  const transitionLines = state.transitions
    .map((transition) => `- ${transition.next_state}: ${transition.condition}`)
    .join('\n');

  return [
    `# Current State: ${state.id}`,
    state.description,
    '',
    '## Instructions',
    instructionLines,
    '',
    '## Sample Phrases (vary naturally, do not repeat verbatim each turn)',
    exampleLines,
    '',
    '## Allowed Transitions',
    transitionLines || '- None (terminal state)',
    '',
    'Before transitioning, ask one concise question that gathers the missing info for the next state.',
    'When the user answer is enough, summarize briefly and transition immediately.',
    '',
    'When the exit condition is met, call set_conversation_state with the next_state.'
  ].join('\n');
}

